import { Path } from "@components/layout/Path";
import AppContext from "@helpers/context";
import ChannelAPI from "@network/api/channel";
import WarehouseAPI from "@network/api/warehouse";
import {
  AwesomeTableComponent,
  Button,
  InputTextSearch,
  Notifications,
  Progress,
} from "d-react-components";
import { debounce, map } from "lodash";
import { useContext, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, generatePath } from "react-router-dom";
import ChannelDetailWarehouseUpdateDrawer from "./ChannelDetailWarehouseUpdateDrawer";

const ChannelDetailWarehouse = () => {
  const { channel } = useContext(AppContext);
  const [openAdd, setOpenAdd] = useState(false);
  const [openRemove, setOpenRemove] = useState(false);
  const { t } = useTranslation();
  const textSearch = useRef("");

  const columns = [
    {
      title: t("name"),
      dataIndex: "name",
      render: (name: string, warehouse: any) => (
        <Link
          to={generatePath(Path.WAREHOUSE_DETAIL, {
            warehouseId: warehouse.id,
          })}
        >
          {name}
        </Link>
      ),
    },
    {
      dataIndex: "warehouse_system_code",
      title: t("systemCode"),
      width: 130,
    },
    {
      dataIndex: "code",
      title: t("navArCode"),
      width: 130,
    },
    {
      dataIndex: "pickup_code",
      title: t("navPickupCode"),
      width: 130,
    },
  ];

  const tableRef = useRef<any>();

  const source = (paging: any) => {
    const body = {
      action: "in",
      id: channel?.id,
      search: textSearch.current,
    };
    return ChannelAPI.warehouseList(body, paging);
  };

  const transformer = (res: any) => {
    return res?.data?.data?.warehouse ?? [];
  };

  const onAddWarehouse = (warehouse: any) => {
    const body = {
      id: channel?.id,
      action: "add",
      warehouse: map(warehouse, (item) => item.id),
    };
    Progress.show(
      { method: ChannelAPI.warehouseUpdate, params: [body] },
      () => {
        setOpenAdd(false);
        tableRef.current.refresh();
        Notifications.showSuccess(t("updateWarehouseSuccess"));
      }
    );
  };

  const onRemoveWarehouse = (warehouse: any) => {
    const body = {
      id: channel?.id,
      action: "remove",
      warehouse: map(warehouse, (item) => item.id),
    };
    Progress.show(
      { method: ChannelAPI.warehouseUpdate, params: [body] },
      () => {
        setOpenRemove(false);
        tableRef.current.refresh();
        Notifications.showSuccess(t("updateWarehouseSuccess"));
      }
    );
  };

  const onChangeTextSearch = debounce((text) => {
    textSearch.current = text;
    tableRef.current.refresh();
  }, 300);

  return (
    <div className="mt-3">
      <div className="d-flex justify-content-between align-items-center">
        <InputTextSearch
          placeholder={t("search")}
          onChange={(e: any) => onChangeTextSearch(e.target.value)}
        />
        <div className="d-flex">
          <Button onClick={() => setOpenRemove(true)} className="ml-2">
            {t("remove")}
          </Button>
          <Button onClick={() => setOpenAdd(true)} className="ml-2">
            {t("add")}
          </Button>
        </div>
      </div>
      <AwesomeTableComponent
        ref={(ref) => {
          tableRef.current = ref;
        }}
        columns={columns}
        source={source}
        transformer={transformer}
        className="mt-3"
      />
      {openAdd && (
        <ChannelDetailWarehouseUpdateDrawer
          open={openAdd}
          onClose={() => setOpenAdd(false)}
          channelId={channel?.id}
          onSave={onAddWarehouse}
          title={t("addWarehouse")}
          type="not_in"
        />
      )}
      {openRemove && (
        <ChannelDetailWarehouseUpdateDrawer
          open={openRemove}
          onClose={() => setOpenRemove(false)}
          channelId={channel?.id}
          onSave={onRemoveWarehouse}
          title={t("removeWarehouse")}
          type="in"
        />
      )}
    </div>
  );
};

export default ChannelDetailWarehouse;
